import { useRef, useState } from 'react';

// Hero promo video with a React-controlled poster overlay.
// Why: the native `poster` attribute is unreliable here — Chromium on Windows
// can decode and display the first text-track cue ("me Vicka.") over the
// poster, and slow/blocked poster fetches leave a black <video> background.
// Rendering the placeholder as a real <img> behind an overlay guarantees the
// user sees the cover image until they press play.
const HeroVideoPlayer = ({ videoSrc, posterSrc, ariaLabel = 'Play promo video' }) => {
  const videoRef = useRef(null);
  const [isIdle, setIsIdle] = useState(true);

  const handlePlay = () => {
    setIsIdle(false);
    const video = videoRef.current;
    if (video) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          setIsIdle(true);
        });
      }
    }
  };

  const handleEnded = () => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
    }
    setIsIdle(true);
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: '750px',
        margin: '0 auto',
        borderRadius: '24px',
        overflow: 'hidden',
        backgroundColor: '#000',
        aspectRatio: '16 / 9',
      }}
    >
      <video
        ref={videoRef}
        onEnded={handleEnded}
        preload="none"
        controls={!isIdle}
        playsInline
        style={{
          width: '100%',
          height: '100%',
          display: 'block',
          objectFit: 'cover',
        }}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {isIdle && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Fallback for the brief window between mount and image load —
            // sampled from the portrait's corner blue so the fade-in doesn't
            // flash black.
            backgroundColor: '#5A8FB8',
          }}
        >
          <img
            src={posterSrc}
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <button
            type="button"
            onClick={handlePlay}
            aria-label={ariaLabel}
            data-testid="hero-video-play-button"
            style={{
              position: 'relative',
              // The frame scales from ~280px on small phones to 750px on desktop.
              // Fixed 88px covered Vicktoria's face at narrow widths. clamp scales
              // with viewport (12vw) between a tap-friendly floor (48px) and the
              // original desktop ceiling (88px), so the button always reads as a
              // portrait-overlay, not a face-cover.
              width: 'clamp(48px, 12vw, 88px)',
              height: 'clamp(48px, 12vw, 88px)',
              borderRadius: '50%',
              border: 'none',
              // 50% alpha so the portrait's face shows through the button.
              // Brand blue still recognizable; circle still reads as a target.
              backgroundColor: 'rgba(74, 142, 179, 0.5)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              transition: 'transform 150ms ease, background-color 150ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(91, 160, 197, 0.85)';
              e.currentTarget.style.transform = 'scale(1.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(74, 142, 179, 0.5)';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onFocus={(e) => {
              e.currentTarget.style.outline = '3px solid #FFFFFF';
              e.currentTarget.style.outlineOffset = '3px';
            }}
            onBlur={(e) => {
              e.currentTarget.style.outline = 'none';
            }}
          >
            {/* Percentages scale the triangle with the button; viewBox keeps
                the intrinsic 32:36 aspect ratio constant so it never squashes. */}
            <svg
              width="36%"
              height="40%"
              viewBox="0 0 32 36"
              aria-hidden="true"
              focusable="false"
            >
              <polygon points="4,2 30,18 4,34" fill="#FFFFFF" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default HeroVideoPlayer;
